import type { Question } from './grammar/topics';
import type { Assignment, StoryMode } from './types';
import { translate } from './translate';

// ─────────────────────────────────────────────────────────────────────────────
// A story as a lesson (AssignmentView's StoryPart, played by GrammarLesson).
// Students go through the WHOLE story, sentence by sentence, in order:
//
//   1. the sentence in the class language → its meaning in their own language
//   2. the meaning in their own language → the sentence in the class language
//
// each one either multiple choice or a word bank to build the sentence from
// (long sentences are always multiple choice: a 30-tile bank is no fun), and
// then the teacher's own questions about the story, if there are any
// (assignments.story_questions, 20261043).
//
// Meanings come from Google (lib/translate.ts), in the browser, one sentence at a
// time. Like every Google answer they are NEVER saved in the database; they're
// kept in this browser only (localStorage), so a second pass doesn't ask again.
// Wrong options and extra word-bank tiles are taken from the story's other
// sentences, so they look right at a glance.
// ─────────────────────────────────────────────────────────────────────────────

export type StoryQuestion = { q: string; options: string[]; answer: number };

/** Word banks are for sentences up to this many words; longer ones are multiple choice. */
const MAX_BANK_WORDS = 12;
const EXTRA_TILES = 3;

/** The story's sentences, in order (paragraph breaks also end one). */
export function sentences(text: string): string[] {
  return text.replace(/\r\n/g, '\n').split(/\n+/)
    .flatMap(p => p.split(/(?<=[.!?…]["»”’)]?)\s+(?=\S)/))
    .map(s => s.replace(/\s+/g, ' ').trim())
    .filter(s => /[\p{L}\p{N}]/u.test(s));
}

/** Teacher questions that are complete enough to ask (a question, 2+ options, the right one picked). */
export function cleanStoryQuestions(x: unknown): StoryQuestion[] {
  if (!Array.isArray(x)) return [];
  return x.flatMap(v => {
    const q = typeof v?.q === 'string' ? v.q.trim() : '';
    const raw: unknown[] = Array.isArray(v?.options) ? v.options : [];
    const options = raw.map(o => (typeof o === 'string' ? o.trim() : ''));
    const answer = Number.isInteger(v?.answer) ? v.answer : -1;
    if (!q || !options[answer]) return [];
    const kept = options.filter(Boolean);
    if (kept.length < 2) return [];
    return [{ q, options: kept, answer: kept.indexOf(options[answer]) }];
  });
}

const words = (s: string) => s.split(/\s+/).filter(Boolean);
/** A word-bank tile: the word without the punctuation around it (answers are checked without punctuation). */
const tile = (w: string) => w.replace(/^[¿¡"“«(]+|[.,;:!?…"”»)]+$/g, '');
const norm = (s: string) => s.toLowerCase().replace(/[¿?¡!.,;:"“”«»؟،()]/g, '').trim();

function shuffle<T>(a: T[], r: () => number): T[] {
  const out = a.slice();
  for (let i = out.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [out[i], out[j]] = [out[j], out[i]]; }
  return out;
}
function rng(seed: number) {
  let s = seed >>> 0;
  return () => { s = (s + 0x6d2b79f5) >>> 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
const hash = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h >>> 0; };

// ── Meanings ────────────────────────────────────────────────────────────────

const cacheKey = (id: string, source: string, target: string, text: string) => `storyMeanings:${id}:${source}:${target}:${hash(text)}`;

/**
 * Each sentence's meaning in the student's language, in order. `onProgress`
 * counts sentences done. Throws if any sentence can't be translated.
 */
export async function meanings(id: string, text: string, source: string, target: string, onProgress?: (done: number, of: number) => void): Promise<string[]> {
  const list = sentences(text);
  const key = cacheKey(id, source, target, text);
  try {
    const hit = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (Array.isArray(hit) && hit.length === list.length && hit.every(x => typeof x === 'string' && x)) return hit;
  } catch { /* none kept */ }
  const out: string[] = new Array(list.length);
  let next = 0, done = 0;
  const worker = async () => {
    while (next < list.length) {
      const i = next++;
      let last: unknown;
      for (let tries = 0; tries < 3 && !out[i]; tries++) {
        try { out[i] = (await translate(list[i], source, target)).text.trim(); }
        catch (e) { last = e; await new Promise(r => setTimeout(r, 600 * (tries + 1))); }
      }
      if (!out[i]) throw last instanceof Error ? last : new Error('Couldn’t get the story ready. Try again.');
      onProgress?.(++done, list.length);
    }
  };
  await Promise.all([worker(), worker(), worker()]);
  try { localStorage.setItem(key, JSON.stringify(out)); } catch { /* full: fine */ }
  return out;
}

// ── The lesson ──────────────────────────────────────────────────────────────

/** Up to `n` other entries, nearest in length first (so a wrong option can't be spotted by its size). */
function others(pool: string[], i: number, n: number, r: () => number): string[] {
  const mine = norm(pool[i]);
  const len = pool[i].length;
  const seen = new Set([mine]);
  const out: string[] = [];
  for (const k of shuffle(pool.map((_, k) => k).filter(k => k !== i), r).sort((a, b) => Math.abs(pool[a].length - len) - Math.abs(pool[b].length - len))) {
    const v = norm(pool[k]);
    if (!v || seen.has(v)) continue;
    seen.add(v); out.push(pool[k]);
    if (out.length >= n) break;
  }
  return out;
}

/** Extra tiles for a word bank: words from other sentences that aren't in this one. */
function extraTiles(answer: string[], pool: string[], i: number, r: () => number): string[] {
  const have = new Set(answer.map(norm));
  const cands = shuffle(pool.filter((_, k) => k !== i).flatMap(words), r);
  const out: string[] = [];
  for (const w of cands) {
    const n = norm(w);
    if (!n || have.has(n)) continue;
    have.add(n); out.push(w);
    if (out.length >= EXTRA_TILES) break;
  }
  return out;
}

// ── Word order ──────────────────────────────────────────────────────────────
// A word bank's sentence can often be built more than one right way: "Karen
// buys the cat and is now very happy" or "...and now is very happy" (a
// student's, and right). Adverbs like these can sit in several places, so the
// answer with ONE of them moved anywhere else counts too. The words must still
// be exactly the right ones, and everything else in its order.
const MOVABLE: Record<string, string[]> = {
  en: ['now', 'also', 'always', 'never', 'often', 'sometimes', 'usually', 'still', 'already', 'just', 'then', 'today', 'tomorrow', 'yesterday', 'here', 'there', 'again', 'too', 'really', 'very', 'soon', 'later', 'finally', 'suddenly', 'together'],
  es: ['ahora', 'también', 'siempre', 'nunca', 'ya', 'todavía', 'aún', 'hoy', 'mañana', 'ayer', 'aquí', 'allí', 'ahí', 'entonces', 'luego', 'después', 'pronto', 'también', 'juntos', 'juntas', 'finalmente', 'otra', 'tampoco', 'solo', 'sólo'],
  pt: ['agora', 'também', 'sempre', 'nunca', 'já', 'ainda', 'hoje', 'amanhã', 'ontem', 'aqui', 'ali', 'lá', 'então', 'depois', 'logo', 'juntos', 'finalmente'],
  fr: ['maintenant', 'aussi', 'toujours', 'jamais', 'déjà', 'encore', "aujourd'hui", 'demain', 'hier', 'ici', 'là', 'alors', 'ensuite', 'puis', 'bientôt', 'ensemble', 'enfin'],
  it: ['ora', 'adesso', 'anche', 'sempre', 'mai', 'già', 'ancora', 'oggi', 'domani', 'ieri', 'qui', 'lì', 'là', 'allora', 'poi', 'presto', 'insieme', 'finalmente'],
  de: ['jetzt', 'auch', 'immer', 'nie', 'schon', 'noch', 'heute', 'morgen', 'gestern', 'hier', 'dort', 'dann', 'bald', 'zusammen', 'endlich', 'wieder'],
};

/** The tiles in the answer's order, plus every order with one movable word moved somewhere else. */
function wordOrders(ws: string[], lang?: string): string[] {
  const out = new Set([ws.join(' ')]);
  const movable = new Set(MOVABLE[(lang ?? 'en').split('-')[0]] ?? MOVABLE.en);
  ws.forEach((w, i) => {
    if (!movable.has(norm(w))) return;
    const rest = ws.filter((_, k) => k !== i);
    for (let j = 0; j <= rest.length; j++) out.add([...rest.slice(0, j), w, ...rest.slice(j)].join(' '));
  });
  return [...out];
}

function exercise(key: string, prompt: string, answer: string, answers: string[], i: number, toNative: boolean, langName: string, r: () => number, lang?: string): Question {
  const ws = words(answer);
  const bank = ws.length >= 2 && ws.length <= MAX_BANK_WORDS && r() < 0.55;
  const instruction = toNative ? `What does it mean in ${langName}?` : `How do you say it in the story?`;
  if (bank) {
    return {
      key, topic: 'custom', kind: 'order', fixed: true, prompt,
      instruction: toNative ? `Build what it means in ${langName}` : 'Build the sentence from the story',
      tiles: shuffle([...ws, ...extraTiles(ws, answers, i, r)].map(tile).filter(Boolean), r),
      // The sentence as written is what's shown as the answer; the tiles' own spelling is accepted too,
      // and so is the same sentence with one movable word (now, also, ahora…) somewhere else.
      answers: [answer, ...wordOrders(ws.map(tile).filter(Boolean), lang)],
    };
  }
  const opts = shuffle([answer, ...others(answers, i, 3, r)], r);
  return { key, topic: 'custom', kind: 'choice', fixed: true, prompt, instruction, options: opts, answers: [answer] };
}

/**
 * The whole lesson. `seed` changes which exercises are word banks and the
 * order of options (a new pass gets a new mix); the sentences always go in order.
 */
export function buildStoryLesson(text: string, means: string[], asked: StoryQuestion[], nativeName: string, seed: number, part?: LessonPart | null, langs?: { source: string; target: string }, oneWay = false): Question[] {
  const src = sentences(text);
  const r = rng(seed);
  const out: Question[] = [];
  // One part of a split lesson: just its sentences (wrong options still come from the whole story),
  // and the teacher's questions only with the last part.
  const [from, to] = partRange(src.length, part);
  src.forEach((s, i) => {
    if (i < from || i >= to) return;
    out.push(exercise(`s${i}-to`, s, means[i], means, i, true, nativeName, r, langs?.target));
    // One way only (story_one_way): never back into the class language.
    if (!oneWay) out.push(exercise(`s${i}-from`, means[i], s, src, i, false, nativeName, r, langs?.source));
  });
  if (part && part.index < part.of - 1) return out;
  asked.forEach((q, k) => {
    out.push({
      key: `q${k}`, topic: 'custom', kind: 'choice', fixed: true, prompt: q.q,
      instruction: 'About the story', options: shuffle(q.options, r), answers: [q.options[q.answer]],
    });
  });
  return out;
}

// ── How each read is done (assignments.story_mode / story_plan, 20261044) ────

export const STORY_MODE_NAME: Record<StoryMode, string> = {
  lesson: 'story lesson', free: 'read freely + lesson', both: 'lesson + timed reading', read: 'timed reading',
};
export const needsLesson = (m: StoryMode) => m !== 'read';
export const needsReadingTime = (m: StoryMode) => m === 'read' || m === 'both';

/** The style of read number `pass` (0 = the first): its own in the plan, the plan's last after that, else story_mode. */
/** Which part of a split story lesson a read covers (assignments.story_split), counting only the reads that have a lesson. */
export type LessonPart = { index: number; of: number };

/** Sentences [from, to) of a part: the story shared out evenly. */
export function partRange(count: number, part?: LessonPart | null): [number, number] {
  if (!part || part.of <= 1) return [0, count];
  return [Math.floor((part.index * count) / part.of), Math.floor(((part.index + 1) * count) / part.of)];
}

/**
 * The part read number `pass` covers, or null for the whole lesson: not split, only one lesson read, or a
 * practice read past the required ones. The parts go to the reads that have a lesson, in order.
 */
export function lessonPartFor(a: Pick<Assignment, 'story_mode' | 'story_plan' | 'required_count' | 'story_split' | 'story_text'>, pass: number): LessonPart | null {
  if (!a.story_split || pass >= a.required_count || !needsLesson(storyModeFor(a, pass))) return null;
  const lessonReads = Array.from({ length: a.required_count }, (_, i) => i).filter(i => needsLesson(storyModeFor(a, i)));
  const of = Math.min(lessonReads.length, sentences(a.story_text ?? '').length);
  const index = lessonReads.indexOf(pass);
  return of > 1 && index >= 0 && index < of ? { index, of } : null;
}

export function storyModeFor(a: Pick<Assignment, 'story_mode' | 'story_plan'>, pass: number): StoryMode {
  const plan = a.story_plan;
  if (plan?.length) return plan[Math.min(pass, plan.length - 1)];
  return a.story_mode ?? 'lesson';
}

/** Every style an assignment uses, for deciding what the form and the preview show. */
export function storyModesUsed(a: Pick<Assignment, 'story_mode' | 'story_plan' | 'required_count'>): StoryMode[] {
  return [...new Set(Array.from({ length: Math.max(1, a.required_count) }, (_, i) => storyModeFor(a, i)))];
}

/** "Read 1: story lesson · reads 2–3: timed reading". */
export function storyPlanLabel(a: Pick<Assignment, 'story_mode' | 'story_plan' | 'required_count'>): string {
  const n = Math.max(1, a.required_count);
  const runs: { from: number; to: number; m: StoryMode }[] = [];
  for (let i = 0; i < n; i++) {
    const m = storyModeFor(a, i), last = runs[runs.length - 1];
    if (last && last.m === m) last.to = i; else runs.push({ from: i, to: i, m });
  }
  if (runs.length === 1) return STORY_MODE_NAME[runs[0].m];
  return runs.map(r => `${r.from === r.to ? `read ${r.from + 1}` : `reads ${r.from + 1}–${r.to + 1}`}: ${STORY_MODE_NAME[r.m]}`).join(' · ');
}
